/**
 * @file: useConversationMembers.ts
 *
 * The room's active members as a single `members$` source of truth — fetched
 * on open and re-fetched when a `members.invited` system message arrives
 * (this hook listens for that itself). Count, the `@mention` candidate list,
 * and any other slice are DERIVED by consumers from `members$`; this hook
 * owns only the list. Empty until first fetched / for a bare (not-yet-created)
 * chat that has no room.
 */

import { useChatSession } from '@euler/app/flows/chat/lib/session/context';
import { ChatRoomActiveMember } from '@euler/model/chat';
import { useServiceFactory } from '@euler/services/factory';
import { onErrorIgnore } from '@euler/utils';
import { useCreation } from '@euler/utils/hooks';
import { useCallback, useEffect } from 'react';
import { BehaviorSubject } from 'rxjs';
import { ChatConversationHandle, isBaked } from '../handle';

export function useConversationMembers(
  conversationHandle: ChatConversationHandle,
): BehaviorSubject<ChatRoomActiveMember[]> {
  const { chatService } = useServiceFactory();
  const { wsClient } = useChatSession();
  const members$ = useCreation(
    () => new BehaviorSubject<ChatRoomActiveMember[]>([]),
    [],
  );
  // Load members for any baked room. A group needs the full roster (count +
  // grid + @mentions); a private room has two members and we need the peer to
  // drive the header's online indicator. Only a bare (not-yet-created) chat
  // has no room to load.
  const roomId = isBaked(conversationHandle) ? conversationHandle.roomId : null;

  const refresh = useCallback(async () => {
    if (!roomId) return;
    try {
      members$.next(await chatService.getRoomActiveMembers(roomId));
    } catch (e) {
      // Non-fatal — keep the last roster; log so the failure isn't silent.
      onErrorIgnore(e);
    }
  }, [roomId, chatService, members$]);

  // Initial load.
  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Roster-while-open: a `members.invited` / `members.removed` system message
  // shifts the roster, so re-fetch (updates the header "(N)" count + the
  // member grid). The hook listens for its own concern's events.
  useEffect(() => {
    if (!roomId) return;
    const sub = wsClient.onRoomEvent(roomId).subscribe(event => {
      if (
        event.kind === 'message.new' &&
        event.type === 'system' &&
        event.body.payload.type === 'notification' &&
        (event.body.payload.notificationType === 'members.invited' ||
          event.body.payload.notificationType === 'members.removed')
      ) {
        void refresh();
      }
    });
    return () => sub.unsubscribe();
  }, [roomId, wsClient, refresh]);

  return members$;
}
